import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { StorageService } from '../_services/storage.service';
import { FilterField as IPWhitelistFilterField } from 'src/app/pages/ip-whitelisting/ip-whitelisting.component';
import { IPWhitelistFormData } from 'src/app/pages/ip-whitelisting/ip-whitelist-dialog/ip-whitelist-dialog.component';
import { WebhookRequest } from 'src/app/pages/webhook-management/webhook-management.component';
import { FilterField as UserFilterField, Status as UserStatus } from 'src/app/pages/user-management/user-management.component'; 
import { ApiCredentialStatus } from 'src/app/pages/api-credential/api-credential.component';
const { DEVELOPER_PORTAL_URL, USER_MANAGEMENT_URL } = environment;

export interface MerchantFilter {
    "columns"?: any;
    "name"?: string,
    "mid"?: string,
    "startDate"?: string,
    "endDate"?: string,
    "sendDate"?: string,
    "pageRecords"?: number,
    "pageNumber"?: number,
    "rid"?:string,
    "Status"?:string,
    "user_type"?:string
}

@Injectable({
providedIn: 'root'
})

export class ApiControllerService {

  constructor(private http: HttpClient, private storageService: StorageService, private router: Router) { }

  // ---------- User APIs ----------
  getUsers(pageSize: number, pageNumber: number, filters: Map<UserFilterField, any>) {
    let url = `${USER_MANAGEMENT_URL}/developer-portal/users?page_size=${pageSize}&page_no=${pageNumber}&sort_column=updatedOn&sort_dir=DESC`;
    const queryParams = this.convertUserFiltersToQueryParams(filters);
    if (queryParams.length > 0) {
      url += '&' + queryParams.join('&');
    }
    return this.http.get(url).pipe(catchError(this.handleError()));
  }

  private convertUserFiltersToQueryParams(filters: Map<UserFilterField, any>) {
    let queryParams: string[] = [];
    filters.forEach((value, key) => {
      switch (key) {
        case UserFilterField.NAME:
          if (value) {
            queryParams.push(`fullName=${value}`);
          }
          break;
        case UserFilterField.EMAIL:
          if (value) {
            queryParams.push(`emailId=${value}`);
          }
          break;
        case UserFilterField.ROLE:
          if (value && value.size > 0) {
            queryParams.push(`role.roleName=$in%3D${Array.from(value).join(',')}`);
          }
          break;
        case UserFilterField.STATUS:
          if (value && value.size > 0) {
            queryParams.push(`status=$in%3D${Array.from(value).join(',')}`);
          }
          break;
        case UserFilterField.MERCHANT_ID:
          if (value) {
            queryParams.push(`merchantId=${value}`);
          }
          break;
        case UserFilterField.MERCHANT_NAME:
          if (value) {
            queryParams.push(`merchantName=${value}`);
          }
          break;
      }
    });
    return queryParams;
  }

  // ---------- Review Signup ----------
  updateSignupRequestResult(userId: string, status: UserStatus, environment: string, merchantId: string | undefined) {
    const url = `${USER_MANAGEMENT_URL}/developer-portal/user/${userId}/status/${status}`;
    let body;
    if(merchantId) {
      body = {
        merchantId: merchantId,
        env: environment
      };
    } else {
      body = null;
    }
    return this.http.put(url, body).pipe(catchError(this.handleError()));
  }

  findMerchant(merchantIdOrName: string) {
    const url = `${DEVELOPER_PORTAL_URL}/merchant?merchantIdOrName=${encodeURIComponent(merchantIdOrName)}`;
    return this.http.get(url).pipe(catchError(this.handleError()));
  }

  // ---------- IP whitelist APIs ----------
  getIPWhitelist(pageSize: number, pageNumber: number, filters: any) {
    let url = `${DEVELOPER_PORTAL_URL}/whitelist-ip?page_size=${pageSize}&page_no=${pageNumber}&sort_column=updatedAt&sort_dir=DESC`;
    const queryParams = this.convertIPWhitelistFiltersToQueryParams(filters);
    if (queryParams.length > 0) {
      url += '&' + queryParams.join('&');
    }
    return this.http.get(url).pipe(catchError(this.handleError()));
  }
  
  private convertIPWhitelistFiltersToQueryParams(filters: Map<IPWhitelistFilterField, any>) {
    let queryParams: string[] = [];
    filters.forEach((value, key) => {
      switch (key) {
        case IPWhitelistFilterField.DATE: {
          // Format NgbDate to YYYY-MM-DD string
          const fromDateStr = `${value.from.year}-${value.from.month.toString().padStart(2, '0')}-${value.from.day.toString().padStart(2, '0')}`;
          const toDateStr = `${value.to.year}-${value.to.month.toString().padStart(2, '0')}-${value.to.day.toString().padStart(2, '0')}`;

          const fromDateTime: string = fromDateStr + ' 00:00:00';
          const toDateTime: string = toDateStr + ' 23:59:59';
          if (fromDateStr && toDateStr) {
            queryParams.push(`updatedAt=gte%3D${fromDateTime}%24lte%3D${toDateTime}`);
          }
          break;
        }
        case IPWhitelistFilterField.STATUS:
          if (value && value.size > 0) {
            queryParams.push(`status=$in%3D${Array.from(value).join(',')}`);
          }
          break;
        case IPWhitelistFilterField.MERCHANT_ID:
          if (value) {
            queryParams.push(`merchantId=${value}`);
          }
          break;
      }
    });
    return queryParams;
  }

  createIPWhitelist(formData: IPWhitelistFormData) {
    const url = `${DEVELOPER_PORTAL_URL}/whitelist-ip`;
    return this.http.post(url, formData).pipe(catchError(this.handleError()));
  }

  updateIPWhitelist(formData: IPWhitelistFormData) {
    const url = `${DEVELOPER_PORTAL_URL}/whitelist-ip/id/${formData.id}`;
    return this.http.put(url, formData).pipe(catchError(this.handleError()));
  }

  getIPWhitelistHistory(pageSize: number, pageNumber: number, merchantId?: string) {
    let url = `${DEVELOPER_PORTAL_URL}/whitelist-ip/history?page_size=${pageSize}&page_no=${pageNumber}&sort_column=eventTime&sort_dir=DESC`;
    if(merchantId) {
      url += `&merchantId=${encodeURIComponent(merchantId)}`;
    }
    return this.http.get(url).pipe(catchError(this.handleError()));
  }

  // ---------- Export file ----------
  exportAllIPWhitelist(filters: Map<IPWhitelistFilterField, any>, columns: { key: string, label: string }[]) {
    // Use Map to guarantee order preservation, then convert to object
    const orderedMap = new Map(columns.map(col => [col.key, col.label]));

    // Convert Map back to object while preserving order
    const columnMapping: { [key: string]: string } = {};
    for (const [key, value] of orderedMap) {
      columnMapping[key] = value;
    }

    // Prepare request body
    const requestBody = {
      columnMapping: columnMapping
    };

    // Build URL with exportType as query parameter
    let url = `${DEVELOPER_PORTAL_URL}/whitelist-ip/export-to-file?sort_dir=DESC&sort_column=updatedAt`;
    const queryParams = this.convertIPWhitelistFiltersToQueryParams(filters);
    if (queryParams.length > 0) {
      url += '&' + queryParams.join('&');
    }
    return this.http.post(url, requestBody).pipe(catchError(this.handleError()));
  }

  exportConfigtHistory(reportType: string, columns: { key: string, label: string }[], merchantId?: string) {
    const orderedMap = new Map(columns.map(col => [col.key, col.label]));
    const columnMapping: { [key: string]: string } = {};
    for (const [key, value] of orderedMap) {
      columnMapping[key] = value;
    }

    // Prepare request body
    const requestBody = {
      columnMapping: columnMapping
    };

    // Build URL with exportType as query parameter
    let url = `${DEVELOPER_PORTAL_URL}/${reportType}/export-to-file?sort_dir=DESC&sort_column=eventTime`;
    if(merchantId) {
      url += `&merchantId=${encodeURIComponent(merchantId)}`;
    }
    return this.http.post(url, requestBody).pipe(catchError(this.handleError()));
  }

  checkFileStatusAndGetDownloadLink(fileName: string) {
    const url = `${DEVELOPER_PORTAL_URL}/export-to-file/${fileName}`;
    return this.http.get(url).pipe(catchError(this.handleError()));
  }

  // ---------- Webhook Management APIs ----------
  getWebhooks(merchantId?: string) {
    let url = `${DEVELOPER_PORTAL_URL}/webhook`;
    if (merchantId) {
      url += `?merchantId=${encodeURIComponent(merchantId)}`;
    }
    return this.http.get(url).pipe(catchError(this.handleError()));
  }
  
  createOrUpdateWebhook(request: WebhookRequest) {
    const url = `${DEVELOPER_PORTAL_URL}/webhook`;
    return this.http.post(url, request).pipe(catchError(this.handleError()));
  }

  getWebhookHistory(pageSize: number, pageNumber: number, merchatnId?: string) {
    let url = `${DEVELOPER_PORTAL_URL}/webhook/history?page_size=${pageSize}&page_no=${pageNumber}&sort_column=eventTime&sort_dir=DESC`;
    if(merchatnId) {
      url += `&merchantId=${encodeURIComponent(merchatnId)}`
    }
    return this.http.get(url).pipe(catchError(this.handleError()));
  }

  // ---------- API Credential APIs ----------

  getApiCredentials(merchantId?: string) {
    let url = `${DEVELOPER_PORTAL_URL}/api-credential?page_size=10&page_no=1&sort_column=createdAt&sort_dir=DESC`;
    if (merchantId) {
      url += `&merchantId=${encodeURIComponent(merchantId)}`;
    }
    return this.http.get(url).pipe(catchError(this.handleError()));
  }

  createApiCredential(merchantId?: string) {
    const url = `${DEVELOPER_PORTAL_URL}/api-credential`;
    const body: any = { label: null };
    if (merchantId) {
      body.merchantId = merchantId;
    }
    return this.http.post(url, body).pipe(catchError(this.handleError()));
  }

  updateApiCredentialStatus(clientId: string, newStatus: ApiCredentialStatus, merchantId?: string) {
    const url = `${DEVELOPER_PORTAL_URL}/api-credential/${clientId}/status`;
    const requestBody: any = { status: newStatus };
    if(merchantId) {
      requestBody.merchantId = merchantId;
    }
    return this.http.patch(url, requestBody).pipe(catchError(this.handleError()));
  }

  rotateApiCredential(clientId: string, rotatedDateTime: string, merchantId?: string) {
    const url = `${DEVELOPER_PORTAL_URL}/api-credential/${clientId}/rotate`;
    const requestBody: any = { rotatedAt: rotatedDateTime };
    if(merchantId) {
      requestBody.merchantId = merchantId;
    }
    return this.http.patch(url, requestBody).pipe(catchError(this.handleError()));
  }

  updateApiCredentialLabel(clientId: string, newLabel: string, merchantId?: string) {
    const url = `${DEVELOPER_PORTAL_URL}/api-credential/${clientId}/label`;
    const requestBody: any = { label: newLabel };
    if(merchantId) {
      requestBody.merchantId = merchantId;
    }
    return this.http.patch(url, requestBody).pipe(catchError(this.handleError()));
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: HttpErrorResponse): Observable<T> => {
      if (error.status == 401) {
        this.storageService.clean()
        this.router.navigate(['/auth/login']);
      }
      result = error?.error;
      return throwError(() => result);
    };
  }

    // Get Support Info
  getSupportInfo(): Observable<any> {
    const url = DEVELOPER_PORTAL_URL.replace(/\/proxy\/developer-portal$/, '/support');
    return this.http.get(url).pipe(catchError(this.handleError()));
  }

  changeSecurityType(changeSecurityType: any): Observable<any> {
    const url = `${USER_MANAGEMENT_URL}/auth/change-security-type`;
    return this.http.post(url, changeSecurityType).pipe(catchError(this.handleError()));
  }

  getSecurityType(): Observable<any> {
    const url = `${USER_MANAGEMENT_URL}/auth/get-security-type`;
    return this.http.get(url).pipe(catchError(this.handleError()));
  }

}